import { css } from '@emotion/react';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

import { Colors, Link, Typography } from '@zapier/design-system';
import { urls, useSize } from '@zapier/identity-utils';
import { logout } from '@zapier/authentication/src/utils/logout';
import { useResendSignupVerification } from '@zapier/authentication/src/hooks/useResendSignupVerification';
import { useTranslations } from 'next-intl';

import {
  AuthLayout,
  CompaniesIntroPromo,
} from '@zapier/auth-pages/src/components/global';
// Imported by its own path rather than the barrel above: this is the control
// under test on this page, and the barrel is stubbed wholesale in the tests.
import { InlineActionButton } from '@zapier/auth-pages/src/components/global/InlineActionButton';
import { fullPageReplace } from '@zapier/auth-pages/src/utils/fullPageReplace';

const textStyle = css`
  ${Typography.Body1}
  color: ${Colors.GrayWarm10};
  text-align: center;
`;

// Focus lands here when "Resend it" unmounts; global.css strips outlines, so draw a ring.
const resendStatusStyle = css`
  &:focus-visible {
    outline: var(--zds-border-focus);
    outline-offset: var(--zds-space-2);
  }
`;

const rootStyle = css`
  display: grid;
  gap: 25px;
`;

const linkRowStyle = css`
  ${Typography.Body1}
  text-align: center;
`;

function Bold(chunks: React.ReactNode) {
  return <b>{chunks}</b>;
}

export function VerifyEmailPage() {
  const router = useRouter();
  const size = useSize();
  const t = useTranslations('verify-email');
  const { status: resendStatus, resend } = useResendSignupVerification();

  const email = (router.query.email as string | undefined) ?? '';

  const resendStatusRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (resendStatus !== 'submitting') {
      return;
    }

    // "Resend it" unmounts under the user and focus falls to <body>; move it here (SC 2.4.3).
    if (document.activeElement === document.body) {
      resendStatusRef.current?.focus();
    }
  }, [resendStatus]);

  // The page is statically pre-rendered, so on the first client render
  // `router.query` is empty even when the URL has `?email=...`. Wait for
  // `router.isReady` before deciding the email is missing.
  const shouldRedirect = router.isReady && !email;

  // A full page load rather than a client-side route change, so the login
  // page's response can set the browser's pre-auth security cookie.
  useEffect(() => {
    if (shouldRedirect) {
      fullPageReplace(urls.LOGIN_URL);
    }
  }, [shouldRedirect]);

  function handleResend() {
    // Ignore re-clicks while a resend is in flight: each resend mints a fresh
    // token (rotating out the previous one) and sends another email, so an
    // un-gated double-click would deliver duplicate emails and invalidate the
    // link in the earlier one.
    if (resendStatus === 'submitting') {
      return;
    }
    void resend(email);
  }

  function ResendLink(chunks: React.ReactNode) {
    return (
      <InlineActionButton onPress={handleResend}>{chunks}</InlineActionButton>
    );
  }

  if (!router.isReady || !email) {
    return null;
  }

  return (
    <AuthLayout
      promo={<CompaniesIntroPromo />}
      showPromo={size !== 'small'}
      title={t('title')}
      titlePosition="right"
    >
      <section css={rootStyle}>
        <p css={textStyle}>
          {t.rich('description', {
            b: Bold,
            email,
          })}
        </p>
        {/* Always mounted so the text swap is announced. */}
        <p
          css={[textStyle, resendStatusStyle]}
          ref={resendStatusRef}
          role="status"
          tabIndex={-1}
        >
          {resendStatus === 'success'
            ? t('resend-sent')
            : resendStatus === 'submitting'
            ? t('resend-sending')
            : resendStatus === 'error'
            ? t.rich('resend-error', {
                resend: ResendLink,
              })
            : t.rich('resend-prompt', {
                resend: ResendLink,
              })}
        </p>
        <p css={linkRowStyle}>
          <Link
            href={urls.LOGIN_URL}
            onPress={() => {
              // Fire-and-forget, and it has to stay that way: `Link` navigates
              // as soon as this handler returns and `PressEvent` has no
              // `preventDefault`, so awaiting would not hold the navigation
              // back — it would just resume after the page is gone. `logout()`
              // sends with `keepalive` so the request survives that instead.
              //
              // `logout()` is `async`, so a synchronous failure clearing the
              // stored auth type surfaces as a rejection too; this `.catch`
              // covers both that and a transport failure. We navigate either
              // way, as before.
              void logout().catch(() => {
                // best effort — the user is navigating away regardless
              });
            }}
          >
            {t('back-to-login')}
          </Link>
        </p>
      </section>
    </AuthLayout>
  );
}
